import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { TenantProfile, UserAccount, UserInput } from '../../../models/userManagement';

const ROLE_OPTIONS = ['APP_ADMIN', 'TENANT_ADMIN', 'TENANT_USER'];

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: UserInput) => Promise<void> | void;
  tenants: TenantProfile[];
  initialValues?: UserAccount;
}

const defaultValues: UserInput = {
  email: '',
  name: '',
  roles: ['TENANT_USER'],
  tenantId: '',
  active: true,
};

const UserDialog: React.FC<UserDialogProps> = ({ open, onClose, onSubmit, tenants, initialValues }) => {
  const [values, setValues] = useState<UserInput>(defaultValues);
  const [submitting, setSubmitting] = useState(false);

  const validationErrors = useMemo(() => {
    const errors: Partial<Record<keyof UserInput, string>> & { roles?: string; tenantId?: string } = {};
    if (!values.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) {
      errors.email = 'Enter a valid email';
    }
    if (!values.roles.length) {
      errors.roles = 'Select at least one role';
    }
    const requiresTenant = values.roles.some((role) => role !== 'APP_ADMIN');
    if (requiresTenant && !values.tenantId) {
      errors.tenantId = 'Tenant is required for tenant-level roles';
    }
    return errors;
  }, [values]);

  const isValid = useMemo(() => Object.values(validationErrors).every((msg) => !msg), [validationErrors]);

  useEffect(() => {
    if (initialValues) {
      setValues({
        email: initialValues.email,
        name: initialValues.name,
        roles: initialValues.roles,
        tenantId: initialValues.tenantId,
        active: initialValues.active,
      });
    } else {
      setValues(defaultValues);
    }
  }, [initialValues, open]);

  const handleChange = (field: keyof UserInput, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isEditMode = Boolean(initialValues);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? 'Edit User' : 'Invite User'}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Email"
              value={values.email}
              onChange={(event) => handleChange('email', event.target.value)}
              required
              type="email"
              disabled={isEditMode}
              error={Boolean(validationErrors.email)}
              helperText={validationErrors.email}
            />
            <TextField
              label="Name"
              value={values.name}
              onChange={(event) => handleChange('name', event.target.value)}
            />
            <FormControl fullWidth error={Boolean(validationErrors.roles)}>
              <InputLabel id="roles-label">Roles</InputLabel>
              <Select
                labelId="roles-label"
                multiple
                label="Roles"
                value={values.roles}
                onChange={(event) => handleChange('roles', event.target.value as string[])}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.roles || 'Assign one or more roles'}
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth error={Boolean(validationErrors.tenantId)}>
              <InputLabel id="tenant-label">Tenant</InputLabel>
              <Select
                labelId="tenant-label"
                label="Tenant"
                value={values.tenantId || ''}
                onChange={(event) => handleChange('tenantId', event.target.value)}
              >
                <MenuItem value="">Global / Multi-tenant</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.tenantId || 'Select tenant for tenant-scoped roles'}
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={values.active ?? true}
                  onChange={(event) => handleChange('active', event.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting || !isValid}>
            {isEditMode ? 'Save' : 'Invite'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default UserDialog;
